import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { MONO_FONT } from '@/theme';
import { SOCIAL_ICON_PATHS } from '@/constants/social';
import { glossaryHref } from '@/libs/content/glosario';
import TipCover, { tipVariant } from './TipCover';

// Iconos decorativos (outline) para el "estilo embed" de Instagram: like,
// comment, save. No son clickeables (aria-hidden) — solo dan el aire de embed.
const EMBED_ICONS = [
  {
    label: 'like',
    d: 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z',
  },
  {
    label: 'comment',
    d: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  },
  { label: 'save', d: 'M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z' },
];

/* Card de un tip, con estilo "embed" de Instagram. La portada (TipCover, según
   `variant`) lleva un icono de IG superpuesto y clickeable → el post. Cada card
   es un <article> con ancla propia (/tips#id). Los #hashtags enlazan al glosario
   (entity graph). Server Component (enlaces con component="a"). */
export default function TipCard({ tip }) {
  const {
    id,
    insight,
    accent,
    dispatch,
    variant,
    text,
    instagramUrl,
    date,
    seeAlso = [],
  } = tip;
  const cv = tipVariant(variant);

  return (
    <Box
      component="article"
      id={id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: '4px',
        p: '16px',
        scrollMarginTop: '96px',
        transition: 'transform 0.18s ease, border-color 0.18s ease',
        '&:hover': { transform: 'translateY(-2px)', borderColor: 'grey.300' },
      }}
    >
      {/* Portada — todo el área enlaza al post de Instagram */}
      <Box
        component="a"
        href={instagramUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Ver en Instagram: ${insight}`}
        sx={{ position: 'relative', display: 'block', textDecoration: 'none' }}
      >
        <TipCover
          insight={insight}
          accent={accent}
          dispatch={dispatch}
          variant={variant}
        />
        {/* Icono IG decorativo (el enlace ya está en el wrapper) */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            width: 34,
            height: 34,
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: cv.chipBg,
            color: cv.chipFg,
            boxShadow: '0 1px 6px rgba(0,0,0,0.18)',
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            sx={{ width: 17, height: 17 }}
          >
            <path d={SOCIAL_ICON_PATHS.instagram} />
          </Box>
        </Box>
      </Box>

      {/* Meta-row: TIP · fecha (izq) · iconos embed decorativos (der) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box
          component="span"
          sx={{
            fontFamily: MONO_FONT,
            fontSize: '10.5px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.muted',
          }}
        >
          TIP · {date}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: 'grey.300',
          }}
          aria-hidden="true"
        >
          {EMBED_ICONS.map((icon) => (
            <Box
              key={icon.label}
              component="svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              sx={{ width: 18, height: 18 }}
            >
              <path d={icon.d} />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Texto de apoyo */}
      {text && (
        <Typography
          sx={{ fontSize: '13.5px', lineHeight: 1.55, color: 'grey.600' }}
        >
          {text}
        </Typography>
      )}

      {/* Footer: #hashtags (izq) · Ver en Instagram ↗ (der) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mt: 'auto',
          pt: 1,
        }}
      >
        {seeAlso.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              fontFamily: MONO_FONT,
              fontSize: '11px',
              letterSpacing: '0.02em',
            }}
          >
            {seeAlso.map((s) => (
              <Box
                key={s.term}
                component="a"
                href={glossaryHref(s.term)}
                sx={{
                  color: 'text.accent',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                #{s.label || s.term}
              </Box>
            ))}
          </Box>
        )}
        <Box
          component="a"
          href={instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            ml: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            fontSize: '12px',
            fontWeight: 500,
            color: 'grey.700',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            transition: 'color 0.15s',
            '&:hover': { color: 'text.accent', textDecoration: 'underline' },
          }}
        >
          Ver en Instagram
          <OpenInNewIcon sx={{ fontSize: '12px' }} aria-hidden="true" />
        </Box>
      </Box>
    </Box>
  );
}
